import React, { useState, useRef } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import uploadService from "../../services/uploadService";
import { toast } from "sonner";

const ImageUpload = ({
  value,
  onChange,
  label,
  required = false,
  className = "",
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const processFile = async (file) => {
    if (!file) return;

    // Validation
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Only JPG, PNG, and WEBP are allowed.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size too large. Max 5MB allowed.");
      return;
    }

    try {
      setIsUploading(true);
      const url = await uploadService.uploadImage(file);
      onChange(url);
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error(error.message || "Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e) => {
    processFile(e.target.files[0]);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const removeImage = () => {
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="text-sm font-medium text-slate-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div 
        className={`flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-4 transition-colors min-h-[160px] ${
          isDragging ? "border-primary bg-primary/5" : "border-slate-200 hover:border-primary/50 bg-slate-50/50"
        }`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {isUploading ? (
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="animate-spin text-primary mb-2" size={32} />
            <span className="text-sm font-medium text-slate-500">
              Uploading to S3...
            </span>
          </div>
        ) : value ? (
          <div className="relative w-full rounded-xl overflow-hidden group bg-slate-100 flex items-center justify-center min-h-[120px]">
            <img
              src={value}
              alt="Uploaded content"
              className="max-h-48 w-auto object-contain"
            />
            <button
              type="button"
              onClick={removeImage}
              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer py-4">
            <Upload className={`w-10 h-10 mb-2 transition-colors ${isDragging ? "text-primary" : "text-slate-400"}`} />
            <span className="text-sm font-medium text-slate-600 text-center">
              Click to upload or drag and drop
            </span>
            <span className="text-xs text-slate-400 mt-1">
              PNG, JPG or WEBP up to 5MB
            </span>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
              ref={fileInputRef}
            />
          </label>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
